import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import CategoryPicker from '../../components/common/CategoryPicker';
import BackButton from '../../components/common/BackButton';

const UNITS = ['kg','quintal','ton','acre','unit'];

export default function CreateRequestPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();
  const prefill   = location.state || {};
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get('/categories')
      .then(({ data }) => setCategories((data.categories||[]).filter(c=>c.isActive)))
      .catch(() => setCategories([]));
  }, []);

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    cropName:          prefill.cropName   || '',
    category:          prefill.category   || '',
    subCategory:       prefill.subCategory|| '',
    quantity:          '',
    quantityUnit:      'kg',
    offeredPrice:      '',
    offeredPriceUnit:  'per_kg',
    preferredLocation: user?.locationAddress || '',
    notes:             '',
    cropId:            prefill.cropId     || '',
  });

  const set = (k,v) => setForm(p => ({ ...p, [k]:v }));

  const submit = async () => {
    if (!form.cropName || !form.quantity) return toast.error('Crop name and quantity are required');
    if (isNaN(form.quantity) || Number(form.quantity) <= 0) return toast.error('Enter a valid quantity');
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.offeredPrice) delete payload.offeredPrice;
      if (!payload.cropId) delete payload.cropId;
      const { data } = await api.post('/requests', payload);
      toast.success(`Request sent! ${data.notifiedFarmers} farmer(s) notified `);
      navigate('/requests/my');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error sending request');
    } finally { setLoading(false); }
  };

  return (
    <div className="page">
      {/* Header */}
      <div className="header header--simple">
        <BackButton />
        <div>
          <div className="header__title">📦 Request Crops</div>
          <div className="header__subtitle">Farmers will be notified instantly</div>
        </div>
      </div>

      <div className="container" style={{ paddingTop:'var(--sp-4)' }}>

        {/* Info banner */}
        <div className="alert alert--info" style={{ marginBottom:'var(--sp-4)' }}>
          <span className="alert__icon">💡</span>
          <div className="alert__body">Matching farmers will get an instant notification and can call you directly.</div>
        </div>

        <div className="card" style={{ padding:16, marginBottom:16 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

            {/* Crop name */}
            <div>
              <label className="label"> Crop Name *</label>
              <input className="input" placeholder="e.g. Carrot, Tomato, Rice" value={form.cropName} onChange={e => set('cropName', e.target.value)} />
            </div>

            {/* Category + Sub — dynamic from admin panel */}
            <CategoryPicker
              categories={categories}
              value={form.category}
              subValue={form.subCategory}
              onChange={v => set('category', v)}
              onSubChange={v => set('subCategory', v)}
            />

            {/* Quantity + Unit */}
            <div>
              <label className="label">⚖️ Quantity Required *</label>
              <div style={{ display:'flex', gap:8 }}>
                <input className="input" type="number" placeholder="e.g. 2000" value={form.quantity} onChange={e => set('quantity', e.target.value)} style={{ flex:2 }} />
                <select className="input" value={form.quantityUnit} onChange={e => set('quantityUnit', e.target.value)} style={{ flex:1 }}>
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            {/* Price offer */}
            <div>
              <label className="label">💰 Price Offer (optional)</label>
              <div style={{ display:'flex', gap:8 }}>
                <div style={{ position:'relative', flex:2 }}>
                  <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'var(--gray-600)', fontSize:14 }}>₹</span>
                  <input className="input" type="number" placeholder="e.g. 25" value={form.offeredPrice} onChange={e => set('offeredPrice', e.target.value)} style={{ paddingLeft:28 }} />
                </div>
                <select className="input" value={form.offeredPriceUnit} onChange={e => set('offeredPriceUnit', e.target.value)} style={{ flex:1 }}>
                  <option value="per_kg">/ kg</option>
                  <option value="per_quintal">/ quintal</option>
                  <option value="per_unit">/ unit</option>
                </select>
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="label"> Preferred Location</label>
              <input className="input" placeholder="City" value={form.preferredLocation} onChange={e => set('preferredLocation', e.target.value)} />
              <p style={{ fontSize:12, color:'var(--gray-400)', marginTop:4 }}>Farmers near this area will be notified first</p>
            </div>

            {/* Notes */}
            <div>
              <label className="label">📝 Additional Notes</label>
              <textarea className="input" rows={3} placeholder="Quality requirements, delivery terms, contact time..." value={form.notes} onChange={e => set('notes', e.target.value)} style={{ resize:'none' }} />
            </div>
          </div>
        </div>

        {/* Preview */}
        {form.cropName && form.quantity && (
          <div className="alert alert--primary" style={{ marginBottom:'var(--sp-4)' }}>
            <span className="alert__icon">📋</span>
            <div className="alert__body">
              Need <strong>{form.quantity} {form.quantityUnit}</strong> of <strong>{form.cropName}</strong>
              {form.offeredPrice && <span> at <strong>₹{form.offeredPrice} {form.offeredPriceUnit?.replace(/_/g,' ')}</strong></span>}
              {form.preferredLocation && <span> near <strong>{form.preferredLocation}</strong></span>}
            </div>
          </div>
        )}

        <button className="btn btn--primary btn--lg" onClick={submit} disabled={loading}>
          {loading ? <span className="spinner spinner--sm spinner--white" /> : '📤 Send Request to Farmers'}
        </button>
        <div style={{ height:'var(--sp-8)' }} />
      </div>
    </div>
  );
}
